import { useQuery } from '@tanstack/react-query';
import { devboxStatusPollInterval } from '@reflex/ui/client/hooks';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Default cadence for {@link useDevboxStatus}. The devbox-status endpoint is a
 * single lightweight Runloop control-plane lookup, so an 8s poll is cheap
 * while still reflecting a suspend/resume promptly on the status indicator.
 */
const DEVBOX_STATUS_POLL_MS = 8_000;

/**
 * Poll the live Runloop devbox status for an agent while it is in view.
 *
 * The sandbox status badge would otherwise infer the devbox state purely from
 * the agent's lifecycle `status`, which can't tell whether the box has
 * auto-suspended after idling (the agent stays `running`/`completed` across a
 * suspend). Polling the real status keeps the indicator honest. The raw status
 * string takes precedence over the inferred state in {@link deriveSandboxState}.
 *
 * While the box is suspended or coming back up the poll runs at a faster
 * cadence (see {@link devboxStatusPollInterval}) so the badge reflects a wake
 * promptly instead of lagging a full {@link DEVBOX_STATUS_POLL_MS} window.
 *
 * `status` is the raw Runloop status string (`running`, `suspended`,
 * `resuming`, …), `null` when the agent has no devbox yet, or `'unknown'` when
 * the lookup couldn't resolve. The same response also carries `resourceSize`,
 * the box's live size, which UI surfaces prefer over the size persisted on the
 * agent row. Polling pauses while the tab is backgrounded.
 */
export function useDevboxStatus(
  agentId: string | null | undefined,
  options?: { enabled?: boolean; intervalMs?: number },
) {
  const enabled = (options?.enabled ?? true) && Boolean(agentId);
  const baseMs = options?.intervalMs ?? DEVBOX_STATUS_POLL_MS;
  return useQuery({
    queryKey: queryKeys.devboxStatus(agentId ?? 'none'),
    queryFn: () => api.getDevboxStatus(agentId!),
    enabled,
    refetchInterval: (query) =>
      enabled ? devboxStatusPollInterval(query.state.data?.status, baseMs) : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
}
